import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { MessageCircle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ImprovementSuggestionsV3Props {
  improvementSuggestions?: string;
  improvedRequest?: string;
  onUseImprovedTemplate?: () => void;
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
  isTransitioning?: boolean;
  transitionDelay?: number;
}

export function ImprovementSuggestionsV3({
  improvementSuggestions,
  improvedRequest,
  onUseImprovedTemplate,
  theme,
  language,
  isTransitioning = false,
  transitionDelay = 0
}: ImprovementSuggestionsV3Props) {
  const { t } = useTranslation('analysis');
  const isDark = theme === 'dark';

  // Если нет данных для отображения, не рендерим компонент
  if (!improvementSuggestions && !improvedRequest) {
    return null;
  }

  return (
    <div className="space-y-6 w-full max-w-full">
      <div className="text-center w-full max-w-full">
        <h2 className={`text-2xl heading-section break-words ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          {language === 'ru' ? 'Решения по улучшению запроса' : 'Query Improvement Tips'}
        </h2>
        <p className={`mt-2 text-lg break-words ${
          isDark ? 'text-slate-400' : 'text-gray-600'
        }`}>
          {t('recommendationsForAnalysis')}
        </p>
      </div>

      <div className="grid gap-6 w-full max-w-full">
        {/* Рекомендации с встроенным улучшенным запросом */}
        {improvementSuggestions && (
          <Card className={`p-4 sm:p-6 min-w-0 w-full max-w-full ${
            isDark 
              ? 'bg-slate-800/50 border-slate-700' 
              : 'bg-white border-gray-200 shadow-lg'
          }`}>
            <div className="relative">
              <div className="flex items-center gap-4 mb-3 min-w-0 w-full">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isDark 
                    ? 'bg-blue-500/20 text-blue-400' 
                    : 'bg-blue-100 text-blue-600'
                }`}>
                  <MessageCircle className="w-6 h-6" />
                </div>
                <h3 className={`text-lg heading-small break-words min-w-0 flex-1 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {language === 'ru' ? 'Рекомендации' : 'Recommendations'}
                </h3>
              </div>
              
              <div className="ml-0 min-w-0 w-full">
                <p className={`text-lg leading-relaxed mb-4 break-words ${
                  isDark ? 'text-slate-200' : 'text-gray-800'
                }`}>
                  {improvementSuggestions}
                </p>
                
                {/* Встроенный улучшенный запрос */}
                {improvedRequest && (
                  <Card className={`p-4 min-w-0 w-full max-w-full ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/50' 
                      : 'bg-white/80 border-gray-200/60 shadow-lg'
                  }`}>
                    <div className="flex items-start gap-3 min-w-0">
                      <ArrowRight className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className={`heading-small mb-2 break-words ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {t('improvedQuery')}
                        </h4>
                        <p className={`text-base leading-relaxed mb-3 break-words ${
                          isDark ? 'text-slate-300' : 'text-gray-700'
                        }`}>
                          {improvedRequest}
                        </p>
                        <p className={`text-sm italic mb-4 break-words ${
                          isDark ? 'text-slate-400' : 'text-green-700'
                        }`}>
                          {language === 'ru' 
                            ? 'Используйте этот шаблон, подставляя свои данные в нужные места'
                            : 'Use this template by substituting your data in the appropriate places'
                          }
                        </p>
                        {onUseImprovedTemplate && (
                          <Button
                            onClick={onUseImprovedTemplate}
                            data-metrika="use-extended-template"
                            className={`w-full sm:w-auto min-w-0 ${
                              isDark 
                                ? 'bg-green-600 hover:bg-green-700 text-white' 
                                : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            <ArrowRight className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="break-words">{t('useThisTemplate')}</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
